import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLES } from "@/config/navigation";
import { ShieldAlert } from "lucide-react";

const TONES = {
    "auth": "bg-blue-50 text-blue-700 border-blue-200",
    "admin": "bg-purple-50 text-purple-700 border-purple-200",
    "sales": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "billing": "bg-amber-50 text-amber-700 border-amber-200",
    "procurement": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "inventory": "bg-zinc-100 text-zinc-700 border-zinc-200",
};
function ActionBadge({ action }) {
    const key = action.split(".")[0];
    const cls = TONES[key] || TONES.inventory;
    return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}>{action}</span>;
}

export default function AuditLogs() {
    const { user } = useAuth();
    const isAdmin = user && ADMIN_ROLES.includes(user.role);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [entity, setEntity] = useState("all");
    const [action, setAction] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (entity !== "all") params.entity = entity;
            if (action !== "all") params.action = action;
            const { data } = await api.get("/audit-logs", { params });
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (isAdmin) { const t = setTimeout(load, 200); return () => clearTimeout(t); } else setLoading(false); }, [q, entity, action, isAdmin]);

    if (!isAdmin) return (
        <div data-testid="page-audit-logs">
            <PageHeader title="Audit Logs" description="Restricted to administrators." />
            <div className="mx-8 my-8 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <ShieldAlert className="h-4 w-4" strokeWidth={1.5} /> You do not have permission to view this page.
            </div>
        </div>
    );

    return (
        <div data-testid="page-audit-logs">
            <PageHeader title="Audit Logs" description="Every meaningful action across YugOS — attributed and time-stamped." />
            <div className="p-8 space-y-4 max-w-[1400px]">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" strokeWidth={1.5} />
                        <Input placeholder="Search email, entity, detail…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="audit-search" />
                    </div>
                    <Select value={entity} onValueChange={setEntity}>
                        <SelectTrigger className="w-40 h-9" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All entities</SelectItem>
                            <SelectItem value="session">Session</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="sales_order">Sales order</SelectItem>
                            <SelectItem value="invoice">Invoice</SelectItem>
                            <SelectItem value="purchase_order">Purchase order</SelectItem>
                            <SelectItem value="product">Product (inventory)</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={action} onValueChange={setAction}>
                        <SelectTrigger className="w-40 h-9" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All actions</SelectItem>
                            <SelectItem value="auth">Auth</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="billing">Billing</SelectItem>
                            <SelectItem value="procurement">Procurement</SelectItem>
                            <SelectItem value="inventory">Inventory</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">{items.length} events</div>
                </div>
                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader><TableRow className="bg-zinc-50/60">
                            {["When", "Action", "Entity", "Actor", "Detail"].map(h => <TableHead key={h} className="text-[10px] uppercase tracking-widest text-zinc-500">{h}</TableHead>)}
                        </TableRow></TableHeader>
                        <TableBody>
                            {loading ? <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                            : items.length === 0 ? <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">No events match your filters.</TableCell></TableRow>
                            : items.map(e => (
                                <TableRow key={e.id} data-testid={`audit-row-${e.id}`}>
                                    <TableCell className="text-xs text-zinc-500 font-mono whitespace-nowrap">{new Date(e.at).toLocaleString()}</TableCell>
                                    <TableCell><ActionBadge action={e.action} /></TableCell>
                                    <TableCell><span className="font-mono text-xs">{e.entity_id || e.entity}</span></TableCell>
                                    <TableCell className="text-xs">{e.actor_email || <span className="text-zinc-400">system</span>}</TableCell>
                                    <TableCell className="text-xs text-zinc-600 max-w-md truncate">{e.detail || "—"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
